import {
  CloseOutlined,
  DownloadOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { Button, notification, Progress } from 'antd';
import { parse } from 'json2csv';
import React, { useRef, useState } from 'react';
import { CSVLink } from 'react-csv';

import { fetchAPI } from '../../../utils/api';
import { useOrderV2QueryParams } from './OrderListingV2';

const notificationKey = 'download-order-v2';
const DownloadOrders = () => {
  const [queryParams] = useOrderV2QueryParams();
  const [downloading, setDownloading] = useState(false);
  const downloadRef = useRef(false);

  const startDownload = async (prevItems = [], page = 1, total = 0) => {
    if (page > 1 && !downloadRef.current) {
      return;
    }

    setDownloading(true);
    downloadRef.current = true;
    let percentage = 0;
    if (total > 0) {
      const totalPages = Math.ceil(total / 10);
      percentage = Math.round(((page - 1) / totalPages) * 100);
    }

    notification.open({
      key: notificationKey,
      message: 'Download Orders',
      description: <Progress percent={percentage} />,
      duration: 0,
      onClose: () => {
        downloadRef.current = false;
        setDownloading(false);
      },
    });

    try {
      const _picks = [
        'id',
        'region_ref_number',
        'project_code',
        'po_reference_number',
        'external_po_number',
        'consolidate_po_number',
        'purchase_request_number',
        'region',
        'assignee_region_identifier',
        'assignee_user_name',
        'buyer_email',
        'buyer_name',
        'entity_name',
        'shipping_address',
        'exchange_rate',
        'total_price_all',
        'total_price',
        'total_price_fulfilled',
        'total_external_remittance',
        'current_tax',
        'all_qty',
        'all_fulfilled_qty',
        'all_fulfilled_amt',
        'handling_fee',
        'cumulative_margin',
        'currency',
        'operation_status',
        'payment_status',
        'invoice_date',
        'issue_date',
        'payment_date',
        'reason',
        'xero_sync_status',
        'is_consolidated_line_items',
        'is_ncpr',
        'created_at',
        'created_by',
        'paid_by',
        'completed_date',
      ];

      const filters = Object.keys(queryParams).reduce((obj, qpKey) => {
        if (qpKey.startsWith('filter_')) {
          const arr = queryParams[qpKey];
          if (!!arr && !!arr.length) {
            return {
              ...obj,
              [qpKey.replace('filter_', '')]: arr,
            };
          }
        }

        return obj;
      }, {});

      const resp = await fetchAPI('order/list/download', {
        method: 'POST',
        payload: {
          page,
          per_page: queryParams.per_page,
          q: queryParams.q,
          q_assignee: queryParams.q_assignee.join(''),
          is_internal_purchase:
            typeof queryParams.is_internal_purchase === 'boolean'
              ? queryParams.is_internal_purchase
              : undefined,
          from_date: !!queryParams.date_start
            ? queryParams.date_start
            : undefined,
          to_date: !!queryParams.date_end ? queryParams.date_end : undefined,
          date_type: queryParams.date_type,
          filters,
          _picks,
        },
      });

      const allItems = [...prevItems, ...resp.items];
      if (resp.total > resp.page * resp.per_page) {
        return startDownload(allItems, page + 1, resp.total);
      } else {
        notification.open({
          key: notificationKey,
          message: 'Finished Download Orders',
          description: (
            <CSVLink filename={'SA Download Orders.csv'} data={parse(allItems)}>
              Click to Download
            </CSVLink>
          ),
          duration: 0,
          onClose: () => {
            downloadRef.current = false;
            setDownloading(false);
          },
        });
      }
    } catch (error) {
      const totalPages = total > 0 ? Math.ceil(total / 10) : 1;
      notification.open({
        key: notificationKey,
        message: 'Failed Download Orders',
        description: (
          <span style={{ color: 'red' }}>
            <CloseOutlined /> Failed download orders on page {page} of{' '}
            {totalPages} pages
          </span>
        ),
        duration: 0,
        onClose: () => {
          downloadRef.current = false;
          setDownloading(false);
        },
      });
    }
  };

  return (
    <Button
      icon={downloading ? <LoadingOutlined /> : <DownloadOutlined />}
      loading={downloading}
      onClick={() => startDownload()}
    >
      Download Orders
    </Button>
  );
};

export default DownloadOrders;
